import { parseSampleRate } from "shell/platform/lib/analytics/amplitudeCore";

const bool = (envVar: string | undefined): boolean => envVar === "true";

export const featureFlags = {
  isProd: import.meta.env.MODE === "prod",
  isDev: import.meta.env.MODE === "preprod",
  enableDemoRoutes: bool(import.meta.env.VITE_WITH_DEMO_ROUTES),
  enableGoogleAnalytics:
    bool(import.meta.env.VITE_ENABLE_GOOGLE_ANALYTICS) &&
    !!import.meta.env.VITE_GOOGLE_ANALYTICS_TAG,
  enableTransactionLifecycle: bool(
    import.meta.env.VITE_TRANSACTION_LIFECYCLE_ENABLED,
  ),
  enableCardSpendLimits: bool(import.meta.env.VITE_ENABLE_CARD_SPEND_LIMITS),
  enableSalesCalculator: bool(import.meta.env.VITE_ENABLE_SALES_CALCULATOR),
  enableDisputes: bool(import.meta.env.VITE_ENABLE_DISPUTES),
  enablePanFinder: bool(import.meta.env.VITE_ENABLE_PAN_FINDER),
  enableWebhookAccess: bool(import.meta.env.VITE_ENABLE_WEBHOOK_ACCESS),
  enableMiddleNameSearch: bool(import.meta.env.VITE_MIDDLE_NAME_SEARCH_ENABLED),
  disableAllMfxCardAction: bool(
    import.meta.env.VITE_DISABLE_ALL_MFX_CARD_ACTION,
  ),
  enableAccessTokenRegionFiltering: bool(
    import.meta.env.VITE_ENABLE_ACCESS_TOKEN_REGION_FILTERING,
  ),
  enableMobileEmailUpdateCheckboxes: bool(
    import.meta.env.VITE_MOBILE_EMAIL_UPDATE_CHECKBOXES,
  ),
  enableUserManagement: bool(import.meta.env.VITE_ENABLE_USER_MANAGEMENT),
  enableGroupManagement: bool(import.meta.env.VITE_ENABLE_GROUP_MANAGEMENT),
  enableOauthManagement: bool(import.meta.env.VITE_ENABLE_OAUTH_MANAGEMENT),
  enableApplications: bool(import.meta.env.VITE_ENABLE_APPLICATIONS),
  enableSsoConfiguration: bool(import.meta.env.VITE_ENABLE_SSO_CONFIGURATION),
  enableCustomerAccountStatement: bool(
    import.meta.env.VITE_ENABLE_CUSTOMER_ACCOUNT_STATEMENT,
  ),
  enableCustomerAccountCommunication: bool(
    import.meta.env.VITE_ENABLE_CUSTOMER_ACCOUNT_COMMUNICATION,
  ),
  enableCustomerAccountCollectionRecoveries: bool(
    import.meta.env.VITE_ENABLE_CUSTOMER_ACCOUNT_COLLECTION_RECOVERIES,
  ),
  enableIncreasedMaxNameLength: bool(
    import.meta.env.VITE_ENABLE_INCREASED_MAX_NAME_LENGTH,
  ),
  enableNewPortalUi: bool(import.meta.env.VITE_ENABLE_NEW_PORTAL_UI),
  enableDE048Drawer: bool(import.meta.env.VITE_ENABLE_DE048_DRAWER),
  enableOrangeFlowDrilldown: bool(
    import.meta.env.VITE_ENABLE_ORANGE_FLOW_DRILLDOWN,
  ),
  enableDE055Drilldown: bool(import.meta.env.VITE_ENABLE_DE055_DRILLDOWN),
  enableVisaMcDe22Drilldown: bool(
    import.meta.env.VITE_ENABLE_VISA_MC_DE22_DRILLDOWN,
  ),
  creditDetails: bool(import.meta.env.VITE_CREDIT_DETAILS),
  creditFeatures: bool(import.meta.env.VITE_CREDIT_FEATURES),
  showSmartClientDeprecation: bool(
    import.meta.env.VITE_ENABLE_SC_DEPRECATION_FEATURES,
  ),
  enable16DigitToken: bool(import.meta.env.VITE_ENABLE_16_DIGIT_TOKEN),
  enableEhi: bool(import.meta.env.VITE_ENABLE_EHI),
  enableClientScoping: bool(import.meta.env.VITE_ENABLE_CLIENT_SCOPING),
  enableFullToken: bool(import.meta.env.VITE_ENABLE_FULL_TOKEN),
  enableSandbox: bool(import.meta.env.VITE_ENABLE_SANDBOX),
  enableApexFields: bool(import.meta.env.VITE_ENABLE_APEX_FIELDS),
  enableOptionalClientFields: bool(
    import.meta.env.VITE_ENABLE_OPTIONAL_CLIENT_FIELDS,
  ),
  enableSponsorGuard: bool(import.meta.env.VITE_ENABLE_SPONSOR_GUARD),
  enableAdminAccounts: bool(import.meta.env.VITE_ENABLE_ADMIN_ACCOUNTS),
  enableAmplitude: bool(import.meta.env.VITE_ENABLE_CONSOLE_AMPLITUDE),
  enableSessionReplay: bool(import.meta.env.VITE_ENABLE_CONSOLE_SESSION_REPLAY),
  enableAiChat:
    bool(import.meta.env.VITE_ENABLE_AI_CHAT) &&
    !!import.meta.env.VITE_AI_CHAT_URL,
} as const;

export const featureFlagDescriptions: Partial<Record<FeatureFlagKey, string>> =
  {
    isProd: "Production environment build.",
    isDev: "Development environment build.",
    enableDemoRoutes: "Exposes demo routes used for internal exploration.",
    enableGoogleAnalytics: "Sends page views and events to Google Analytics.",
    enableTransactionLifecycle:
      "Shows the transaction lifecycle view on transaction details.",
    enableCardSpendLimits: "Enables card-level spend limit controls.",
    enableSalesCalculator: "Shows the Sales Calculator in the Portal.",
    enableDisputes: "Enables the disputes workflow.",
    enablePanFinder: "Enables the PAN Finder lookup tool.",
    enableWebhookAccess: "Enables the webhook access management pages.",
    enableMiddleNameSearch:
      "Allows searching customers by middle name as well as first/last.",
    disableAllMfxCardAction:
      "Hides all MFX card actions from the card details screen.",
    enableAccessTokenRegionFiltering:
      "Filters access tokens by the currently selected region.",
    enableMobileEmailUpdateCheckboxes:
      "Shows the mobile and email update checkboxes when editing cardholder details.",
    enableUserManagement: "Enables the user management pages.",
    enableGroupManagement: "Enables the group management pages.",
    enableOauthManagement: "Enables the OAuth application management pages.",
    enableApplications: "Enables the applications section.",
    enableSsoConfiguration: "Enables the SSO configuration pages.",
    enableCustomerAccountStatement:
      "Enables the customer account statement view.",
    enableCustomerAccountCommunication:
      "Enables the customer account communication view.",
    enableCustomerAccountCollectionRecoveries:
      "Enables the collections and recoveries workflow for customer accounts.",
    enableIncreasedMaxNameLength:
      "Allows an increased maximum length for cardholder name fields.",
    enableNewPortalUi: "Switches the Portal to the new UI.",
    enableDE048Drawer:
      "Shows the DE048 additional-data drawer on transaction details.",
    enableOrangeFlowDrilldown:
      "Enables the orange-flow drill-down on transaction details.",
    enableDE055Drilldown:
      "Enables the DE055 drill-down on transaction details.",
    enableVisaMcDe22Drilldown:
      "Enables the Visa/Mastercard DE22 drill-down on transaction details.",
    creditDetails: "Shows credit account details.",
    creditFeatures: "Enables credit product features.",
    showSmartClientDeprecation:
      "Shows Smart Client deprecation notices in the UI.",
    enable16DigitToken: "Allows 16-digit token generation.",
    enableEhi: "Enables EHI (Embedded Host Interface) configuration.",
    enableClientScoping: "Enables the client scoping selector.",
    enableFullToken: "Reveals the full token value in supported views.",
    enableSandbox: "Enables the sandbox app.",
    enableApexFields:
      "Shows legacy APEX fields (programme manager, institution) in client-lifecycle.",
    enableOptionalClientFields:
      "Shows the optional client fields (contact, address, phone, email, reporting currency/timezone/cutoff) in client-lifecycle create/edit forms. Off by default: forms show only the minimal fields (MSA: name; SUB: name + category).",
    enableSponsorGuard:
      "Enables the Sponsor Guard routes and menu. While on, the Sponsor Guard roles are also injected into the decoded roles until the IdP issues them.",
    enableAdminAccounts: "Enables the Admin Accounts routes and menu.",
    enableAmplitude:
      "Enables Amplitude product analytics for the console. Also needs VITE_CONSOLE_AMPLITUDE_API_KEY.",
    enableSessionReplay:
      "Records Amplitude Session Replay (masked) for the console. Only applies when enableAmplitude is on; VITE_CONSOLE_SESSION_REPLAY_SAMPLE_RATE sets the share of sessions recorded.",
    enableAiChat:
      "Shows the floating AI assistant chat on Portal pages. Requires VITE_AI_CHAT_URL.",
  };

export const featureConfig = {
  googleAnalyticsTag: import.meta.env.VITE_GOOGLE_ANALYTICS_TAG ?? "",
  amplitudeApiKey: import.meta.env.VITE_CONSOLE_AMPLITUDE_API_KEY ?? "",
  sessionReplaySampleRate: parseSampleRate(
    import.meta.env.VITE_CONSOLE_SESSION_REPLAY_SAMPLE_RATE,
  ),
  backOfficeUrl: import.meta.env.VITE_BACK_OFFICE_URL ?? "",
  panFinderTimeoutLength:
    Number(import.meta.env.VITE_PAN_FINDER_TIMEOUT_LENGTH) || 30,
  additionalAlwaysIncludedAuthorisors:
    import.meta.env.VITE_ADDITIONAL_ALWAYS_INCLUDED_AUTHORISORS ?? "",
  aiChatUrl: import.meta.env.VITE_AI_CHAT_URL ?? "",
} as const;

export type FeatureFlagKey = keyof typeof featureFlags;
